import { useState } from "react";
import { FiCopy, FiEdit2, FiMove, FiPlus, FiTrash2 } from "react-icons/fi";

import Modal, { ConfirmModal } from "../common/Modal";
import SetFormModal from "./SetFormModal";
import { ErrorMessage } from "../common/Feedback";
import { programs as programsApi } from "../../api/services";
import { describeLoad, formatReps } from "../../utils/setFormat";
import { useI18n } from "../../i18n/I18nContext";
import { sessionTitle } from "../../utils/sessionTitle";

const exerciseLabel = (set, locale) =>
  set.exerciseLabels?.[locale] || set.exerciseLabels?.en || set.exerciseSlug;

/**
 * A private MIME type, so a session only accepts sets.
 *
 * It also makes the drag readable during dragover, where the browser hides the
 * payload itself and exposes nothing but the list of types - which is all that
 * is needed to decide whether to light a row up.
 */
const DRAG_TYPE = "application/x-sf-set";

/** The same, for whole sessions. Separate, so the two drags never cross. */
const DAY_DRAG_TYPE = "application/x-sf-day";

/**
 * One session in edit mode: its sets as an editable list, plus the actions that
 * change the session itself.
 *
 * This is the authoring counterpart of SessionDay (which renders a session for
 * training, with computed loads). They're kept apart on purpose - a coach
 * writing a program cares about the prescription, an athlete running it cares
 * about the weight, and merging both into one component would mean every row
 * carrying two sets of controls.
 */
export default function SessionEditor({ clubId, programId, day, place, locale, onChanged }) {
  const { t } = useI18n();
  const [editingSet, setEditingSet] = useState(null);
  const [deletingSet, setDeletingSet] = useState(null);
  const [editingDay, setEditingDay] = useState(false);
  const [deletingDay, setDeletingDay] = useState(false);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(null);
  // Where the dragged set would land: the row being hovered and which side of
  // it. Kept per session, so two cards never both draw an insertion line.
  const [dropAt, setDropAt] = useState(null);
  const [busy, setBusy] = useState(false);
  // Where a dragged session would land relative to this one.
  const [dropDay, setDropDay] = useState(null);

  const removeSet = async () => {
    try {
      await programsApi.removeSet(clubId, programId, deletingSet.id);
      setDeletingSet(null);
      onChanged();
    } catch (err) {
      setError(err);
      setDeletingSet(null);
    }
  };

  const duplicateSet = async (set) => {
    setBusy(true);
    try {
      await programsApi.duplicateSet(clubId, programId, set.id);
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const startDrag = (event, set) => {
    event.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ setId: set.id, dayId: day.id }));
    event.dataTransfer.effectAllowed = "move";
    setDragging(set.id);
  };

  const dragOverRow = (event, index) => {
    if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    // The half of the row the pointer is in decides which side of it the set
    // lands on, which is what makes the last slot of a list reachable.
    const box = event.currentTarget.getBoundingClientRect();
    const after = event.clientY > box.top + box.height / 2;
    setDropAt({ index, after });
  };

  // The side of the row is read from the drop itself rather than from the
  // hover state: a quick drag can be dropped on a row before React has
  // rendered the dragover that would have recorded which half it was over.
  const drop = async (event, index, after) => {
    if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
    event.preventDefault();
    setDropAt(null);
    setDragging(null);

    if (after === undefined) {
      const box = event.currentTarget.getBoundingClientRect();
      after = event.clientY > box.top + box.height / 2;
    }

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData(DRAG_TYPE));
    } catch {
      return;
    }
    if (!payload?.setId) return;

    // The index is counted in the list the set is not part of, which is the
    // list the API inserts into - otherwise a set dragged downwards inside its
    // own session lands one slot short of where it was dropped.
    const sameDay = payload.dayId === day.id;
    const from = sets.findIndex((set) => set.id === payload.setId);
    let target = index + (after ? 1 : 0);
    if (sameDay && from >= 0 && from < target) target -= 1;
    if (sameDay && from === target) return;

    setBusy(true);
    try {
      await programsApi.moveSet(clubId, programId, payload.setId, day.id, target + 1);
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const duplicateDay = async () => {
    setBusy(true);
    try {
      await programsApi.duplicateDay(clubId, programId, day.id);
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const startDayDrag = (event) => {
    event.dataTransfer.setData(DAY_DRAG_TYPE, JSON.stringify({ dayId: day.id }));
    event.dataTransfer.effectAllowed = "move";
  };

  const dayDragOver = (event) => {
    if (!event.dataTransfer.types.includes(DAY_DRAG_TYPE)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    const box = event.currentTarget.getBoundingClientRect();
    setDropDay({ after: event.clientY > box.top + box.height / 2 });
  };

  // A session dropped onto another session's header takes that one's week,
  // and the place just before or after it.
  const dropOnDay = async (event) => {
    if (!event.dataTransfer.types.includes(DAY_DRAG_TYPE)) return;
    event.preventDefault();
    const box = event.currentTarget.getBoundingClientRect();
    const after = event.clientY > box.top + box.height / 2;
    setDropDay(null);

    let payload;
    try {
      payload = JSON.parse(event.dataTransfer.getData(DAY_DRAG_TYPE));
    } catch {
      return;
    }
    if (!payload?.dayId || payload.dayId === day.id) return;

    setBusy(true);
    try {
      await programsApi.moveDay(clubId, programId, payload.dayId, day.week, place + (after ? 1 : 0));
      onChanged();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const removeDay = async () => {
    try {
      await programsApi.removeDay(clubId, programId, day.id);
      setDeletingDay(false);
      onChanged();
    } catch (err) {
      setError(err);
      setDeletingDay(false);
    }
  };

  const sets = day.sets || [];

  return (
    <div className="sf-card">
      <div
        className={[
          "sf-row-between",
          "sf-session-header",
          dropDay ? (dropDay.after ? "sf-drop-after" : "sf-drop-before") : "",
        ]
          .filter(Boolean)
          .join(" ")}
        onDragOver={dayDragOver}
        onDragLeave={() => setDropDay(null)}
        onDrop={dropOnDay}
      >
        <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
          {/* Only the grip is draggable: the header holds buttons, and a
              header that is itself draggable turns every stray click on one
              into the start of a drag. */}
          <span
            className="sf-drag-handle"
            draggable
            onDragStart={startDayDrag}
            onDragEnd={() => setDropDay(null)}
            title={t("programs.reorderSession")}
            aria-hidden="true"
          >
            <FiMove />
          </span>
          {t("programs.week", { week: day.week })} · {t("programs.day", { day: day.day })}
          {sessionTitle(day) ? <span className="sf-session-name"> · {sessionTitle(day)}</span> : null}
        </h3>
        <div className="sf-row" style={{ gap: "0.25rem" }}>
          <button
            className="sf-button sf-button-ghost sf-button-sm"
            onClick={duplicateDay}
            disabled={busy}
            title={t("programs.duplicateSession")}
          >
            <FiCopy /> {t("common.duplicate")}
          </button>
          <button className="sf-button sf-button-ghost sf-button-sm" onClick={() => setEditingDay(true)}>
            <FiEdit2 /> {t("common.edit")}
          </button>
          <button className="sf-button sf-button-ghost sf-button-sm" onClick={() => setDeletingDay(true)}>
            <FiTrash2 /> {t("common.delete")}
          </button>
        </div>
      </div>

      <ErrorMessage error={error} />

      <div className="sf-table-wrapper" style={{ marginTop: "0.6rem" }}>
        <table className="sf-table">
          <thead>
            <tr>
              <th style={{ width: 28 }} />
              <th>{t("session.exercise")}</th>
              <th className="sf-table-num">{t("session.reps")}</th>
              <th>{t("session.loadMode")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sets.map((set, index) => (
              <tr
                key={set.id}
                draggable
                onDragStart={(event) => startDrag(event, set)}
                onDragEnd={() => {
                  setDragging(null);
                  setDropAt(null);
                }}
                onDragOver={(event) => dragOverRow(event, index)}
                onDragLeave={() => setDropAt((at) => (at?.index === index ? null : at))}
                onDrop={(event) => drop(event, index)}
                className={[
                  dragging === set.id ? "sf-row-dragging" : "",
                  dropAt?.index === index ? (dropAt.after ? "sf-drop-after" : "sf-drop-before") : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <td className="sf-drag-handle" aria-hidden="true">
                  <FiMove />
                </td>
                <td>
                  {exerciseLabel(set, locale)}
                  {set.notes ? <div className="sf-muted">{set.notes}</div> : null}
                </td>
                <td className="sf-table-num">{formatReps(set)}</td>
                <td className="sf-muted">{describeLoad(t, set)}</td>
                <td className="sf-table-num">
                  <div className="sf-row" style={{ justifyContent: "flex-end", gap: "0.25rem" }}>
                    <button
                      className="sf-button sf-button-ghost sf-button-sm"
                      onClick={() => duplicateSet(set)}
                      disabled={busy}
                      aria-label={t("programs.duplicateSet")}
                      title={t("programs.duplicateSet")}
                    >
                      <FiCopy />
                    </button>
                    <button
                      className="sf-button sf-button-ghost sf-button-sm"
                      onClick={() => setEditingSet(set)}
                      aria-label={t("common.edit")}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      className="sf-button sf-button-ghost sf-button-sm"
                      onClick={() => setDeletingSet(set)}
                      aria-label={t("common.delete")}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {/* A session with nothing in it still has to be a target, or a set
                can never be moved back into an emptied day. */}
            <tr
              className={dropAt?.index === -1 ? "sf-drop-before" : ""}
              onDragOver={(event) => {
                if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = "move";
                setDropAt({ index: -1, after: false });
              }}
              onDragLeave={() => setDropAt((at) => (at?.index === -1 ? null : at))}
              onDrop={(event) => drop(event, sets.length - 1, true)}
            >
              <td colSpan={5} className="sf-muted sf-drop-rest">
                {sets.length === 0 ? t("programs.dropSetHere") : ""}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <button
        className="sf-button sf-button-secondary sf-button-sm"
        style={{ marginTop: "0.6rem" }}
        onClick={() => setEditingSet({})}
      >
        <FiPlus /> {t("programs.addSet")}
      </button>

      {editingSet ? (
        <SetFormModal
          clubId={clubId}
          programId={programId}
          day={day}
          set={editingSet.id ? editingSet : null}
          onClose={() => setEditingSet(null)}
          onSaved={() => {
            setEditingSet(null);
            onChanged();
          }}
        />
      ) : null}

      {deletingSet ? (
        <ConfirmModal
          title={t("common.delete")}
          message={t("programs.confirmDeleteSet")}
          onConfirm={removeSet}
          onClose={() => setDeletingSet(null)}
        />
      ) : null}

      {editingDay ? (
        <SessionFormModal
          clubId={clubId}
          programId={programId}
          day={day}
          onClose={() => setEditingDay(false)}
          onSaved={() => {
            setEditingDay(false);
            onChanged();
          }}
        />
      ) : null}

      {deletingDay ? (
        <ConfirmModal
          title={t("common.delete")}
          message={t("programs.confirmDeleteSession", { week: day.week, day: day.day })}
          onConfirm={removeDay}
          onClose={() => setDeletingDay(false)}
        />
      ) : null}
    </div>
  );
}

/** Creates or renumbers a session. */
export function SessionFormModal({ clubId, programId, day, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState({
    week: day?.week ?? "",
    day: day?.day ?? "",
    // sessionTitle, not day.title: an unnamed session is stored as "Week 1
    // Day 3", and prefilling that would save it as a title the coach chose
    // the moment the session is renumbered.
    title: sessionTitle(day),
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    const payload = {
      week: form.week === "" ? 0 : Number(form.week),
      day: form.day === "" ? 0 : Number(form.day),
      title: form.title,
    };
    try {
      if (day) await programsApi.updateDay(clubId, programId, day.id, payload);
      else await programsApi.addDay(clubId, programId, payload);
      onSaved();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  // Adding a session with the numbers left blank continues the program's own
  // numbering server-side, which is what a coach filling a block wants.
  const valid = day ? Number(form.week) > 0 && Number(form.day) > 0 : true;

  return (
    <Modal
      title={day ? t("programs.editSession") : t("programs.addSession")}
      onClose={onClose}
      actions={
        <>
          <button className="sf-button sf-button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button className="sf-button" onClick={submit} disabled={busy || !valid}>
            {t("common.save")}
          </button>
        </>
      }
    >
      <div className="sf-row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <div className="sf-field" style={{ flex: 1, minWidth: 110 }}>
          <label className="sf-label">
            {t("session.week")}
            {day ? "" : ` (${t("common.optional")})`}
          </label>
          <input className="sf-input" type="number" min="1" value={form.week} onChange={set("week")} />
        </div>
        <div className="sf-field" style={{ flex: 1, minWidth: 110 }}>
          <label className="sf-label">
            {t("session.day")}
            {day ? "" : ` (${t("common.optional")})`}
          </label>
          <input className="sf-input" type="number" min="1" value={form.day} onChange={set("day")} />
        </div>
      </div>
      <div className="sf-field">
        <label className="sf-label">
          {t("session.title")} <span className="sf-muted">({t("common.optional")})</span>
        </label>
        <input className="sf-input" value={form.title} onChange={set("title")} />
      </div>
      <ErrorMessage error={error} />
    </Modal>
  );
}
